/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import { gnoment, gnomentToCalendarDate } from 'shared-ui/gnoment';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { FlexRow, FlexCol, Label, DatePicker, localToday } from 'sarsaparilla';
import { parseDate } from '@internationalized/date';

const propTypes = {
    groupMembers: PropTypes.arrayOf(PropTypes.object),
    handleGroupMemberChange: PropTypes.func,
    collectBirthdate: PropTypes.bool,
    collectAge: PropTypes.bool,
    isDisabled: PropTypes.bool,
};

const defaultProps = {
    collectBirthdate: true,
    collectAge: false,
    isDisabled: false,
};

export default class PermitHolderIdentification extends React.Component {
    static propTypes = propTypes;

    static defaultProps = defaultProps;

    constructor(props) {
        super(props);
        this.handleChange = this.handleChange.bind(this);
        this.handleDatePickerChange = this.handleDatePickerChange.bind(this);
        this.getDatePickerValue = this.getDatePickerValue.bind(this);
        this.datePickerValidator = this.datePickerValidator.bind(this);
    }

    state = {
        datePickerValue: null,
    };

    handleChange(event) {
        const newMembers = [...this.props.groupMembers];
        newMembers[0] = { ...newMembers[0] };
        if (this.props.collectAge) {
            const parse = parseInt(event.target.value, 10);
            if (!isNaN(parse)) {
                newMembers[0].age = event.target.value;
            }
        } else if (this.props.collectBirthdate) {
            newMembers[0].birthdate = event.target.value;
        }
        this.props.handleGroupMemberChange(newMembers);
    }

    handleDatePickerChange(value, validation) {
        const fauxEvent = {
            target: {
                value: '',
            },
        };

        if (value && !validation.isInvalid) {
            const [year, month, day] = value.toString().split('-');

            fauxEvent.target.value = `${month}/${day}/${year}`;
        }

        this.setState({
            datePickerValue: value,
        });

        this.handleChange(fauxEvent);
    }

    getDatePickerValue(value) {
        if (value) {
            if (value.includes('/')) {
                const [month, day, year] = value.split('/');

                return parseDate(`${year}-${month}-${day}`);
            }
            //Sometimes date comes in like this Wed Aug 14 2024 12:00:00 GMT+0000
            return gnomentToCalendarDate(gnoment(value));
        }

        return this.state.datePickerValue;
    }

    isDateTooFarInThePast(dateToCompare) {
        const MAX_PAST_DATE = localToday.subtract({ years: 120 });

        return dateToCompare.compare(MAX_PAST_DATE) < 0;
    }

    isDateInTheFuture(dateToCompare) {
        return dateToCompare.compare(localToday) > 0;
    }

    datePickerValidator(value, label) {
        const isPastDate = this.isDateTooFarInThePast(value);
        const isFutureDate = this.isDateInTheFuture(value);
        let errorMessage = '';

        if (isPastDate) {
            errorMessage = `${label} is too far in the past.`;
        }

        if (isFutureDate) {
            errorMessage = `${label} can not be in the future.`;
        }

        return {
            isInvalid: isPastDate || isFutureDate,
            errorMessage,
        };
    }

    render() {
        if (!this.props.groupMembers) {
            return null;
        }
        let inputGroup;
        if (this.props.collectAge) {
            const value = get(this.props, 'groupMembers[0].age', '');
            inputGroup = (
                <div className="rec-form-item-wrap">
                    <Label htmlFor="memberAge" isRequired>
                        Age
                    </Label>
                    <input
                        id="memberAge"
                        type="number"
                        min="1"
                        max="999"
                        step="1"
                        placeholder="Please enter your age"
                        value={value}
                        onChange={this.handleChange}
                        readOnly={this.props.isDisabled}
                        disabled={this.props.isDisabled}
                    />
                </div>
            );
        } else if (this.props.collectBirthdate) {
            const birthdate = get(this.props, 'groupMembers[0].birthdate', null);

            inputGroup = (
                <div className="rec-form-item-wrap">
                    <DatePicker
                        autoOpenCalendar={false}
                        isReadOnly={this.props.isDisabled}
                        isDisabled={this.props.isDisabled}
                        label="Birth Date"
                        id="memberBirthdate"
                        value={this.getDatePickerValue(birthdate)}
                        onChange={this.handleDatePickerChange}
                        validator={this.datePickerValidator}
                    />
                </div>
            );
        } else {
            return null;
        }
        return (
            <div className="per-holder-id-form per-section-group">
                <div className="rec-order-detail-subsection-title">
                    Permit Holder Identification
                </div>
                <FlexRow>
                    <FlexCol md={4}>{inputGroup}</FlexCol>
                </FlexRow>
            </div>
        );
    }
}
